/**
 * BillingProfileForm — F-002 Phase 4 (IMPL-P4-008).
 *
 * Form master con React Hook Form + zodResolver(billingProfileUpsertInputSchema)
 * e tabbed UI (Anagrafica / Fatturazione / IBAN). La tab attiva è sincronizzata
 * con il query param `?tab=` via `useSearchParams` di react-router-dom.
 *
 * Submit flow:
 *  1. Se `hasFattureEmesse === true` e sdiCode/sdiPec sono cambiati rispetto
 *     al profilo salvato → apre `<SdiChangeConfirmDialog>` e attende conferma.
 *  2. Altrimenti chiama `save(values)` dal `useBillingProfile()` provider.
 *  3. Su successo: refresh del profilo (già fatto da `save`) + messaggio
 *     "Profilo salvato" come stato locale (no toast lib disponibile in V1).
 *
 * Tabs implementate senza `@radix-ui/react-tabs` (non installato): pulsanti
 * con `role="tab"` + pannelli `role="tabpanel"`. Accessibilità garantita da
 * `aria-selected` e `aria-controls`.
 */
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'react-router-dom';
import { billingProfileUpsertInputSchema, type BillingProfileUpsertInput } from '@app/shared';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

import { useBillingProfile } from '../hooks/useBillingProfile';
import { isProfiloApiError } from '../lib/profilo-client';
import { AnagraficaSection } from './AnagraficaSection';
import { FatturazioneSection } from './FatturazioneSection';
import { IbanSection } from './IbanSection';
import { SdiChangeConfirmDialog } from './SdiChangeConfirmDialog';

type TabKey = 'anagrafica' | 'fatturazione' | 'iban';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'anagrafica', label: 'Anagrafica' },
  { key: 'fatturazione', label: 'Fatturazione' },
  { key: 'iban', label: 'IBAN' },
];

const VALID_TAB_KEYS = new Set<TabKey>(['anagrafica', 'fatturazione', 'iban']);

function buildDefaultValues(
  profile: ReturnType<typeof useBillingProfile>['profile'],
): Partial<BillingProfileUpsertInput> {
  if (!profile) {
    return { type: 'PF', nazione: 'IT' } as Partial<BillingProfileUpsertInput>;
  }

  // Mappa il BillingProfileView (server, IBAN masked) sul tipo di input upsert
  // (client, IBAN plaintext opzionale — vuoto in modifica perché il server non
  // ce lo restituisce in chiaro).
  const base = {
    indirizzo: profile.indirizzo ?? '',
    cap: profile.cap ?? '',
    citta: profile.citta ?? '',
    provincia: profile.provincia ?? '',
    nazione: profile.nazione ?? 'IT',
  };

  if (profile.type === 'PG') {
    return {
      type: 'PG',
      ragioneSociale: profile.ragioneSociale ?? '',
      piva: profile.piva ?? '',
      sdiCode: profile.sdiCode ?? undefined,
      sdiPec: profile.sdiPec ?? undefined,
      paymentMethodPreference: profile.paymentMethodPreference ?? undefined,
      ...base,
    } as Partial<BillingProfileUpsertInput>;
  }

  return {
    type: 'PF',
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    codiceFiscale: profile.codiceFiscale ?? '',
    paymentMethodPreference: profile.paymentMethodPreference ?? undefined,
    ...base,
  } as Partial<BillingProfileUpsertInput>;
}

export function BillingProfileForm() {
  const { profile, hasFattureEmesse, save } = useBillingProfile();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = useMemo<TabKey>(() => {
    const candidate = searchParams.get('tab');
    if (candidate && VALID_TAB_KEYS.has(candidate as TabKey)) {
      return candidate as TabKey;
    }
    return 'anagrafica';
  }, [searchParams]);

  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [sepaAuthorized, setSepaAuthorized] = useState<boolean>(false);
  const [pendingValues, setPendingValues] = useState<BillingProfileUpsertInput | null>(null);

  const defaultValues = useMemo(() => buildDefaultValues(profile), [profile]);

  const form = useForm<BillingProfileUpsertInput>({
    resolver: zodResolver(billingProfileUpsertInputSchema),
    mode: 'onTouched',
    defaultValues: defaultValues as BillingProfileUpsertInput,
  });

  const { handleSubmit, reset, formState } = form;

  // Quando il profilo cambia (refresh post-save o caricamento iniziale), resetta
  // il form con i nuovi valori. Senza questo, il form mostra default vuoti dopo
  // il primo render asincrono.
  useEffect(() => {
    reset(buildDefaultValues(profile) as BillingProfileUpsertInput);
  }, [profile, reset]);

  // Sincronizza tab → query string (non aggiunge entry nello history stack).
  function selectTab(tab: TabKey) {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  }

  function sdiChanged(values: BillingProfileUpsertInput): boolean {
    if (values.type !== 'PG') return false;
    const prevCode = profile?.sdiCode ?? null;
    const prevPec = profile?.sdiPec ?? null;
    return (values.sdiCode ?? null) !== prevCode || (values.sdiPec ?? null) !== prevPec;
  }

  async function persist(values: BillingProfileUpsertInput) {
    setServerError(null);
    setSuccess(false);
    try {
      await save(values);
      setSuccess(true);
      // TODO(F-011): se sepaAuthorized && values.iban → POST /api/consent-records
      // con { type: 'SEPA_AUTHORIZATION', iban_masked: ... }. L'endpoint non
      // esiste ancora in V1; lasciato come no-op coordinato qui per evitare
      // che IbanSection chiami il client direttamente.
    } catch (err) {
      if (isProfiloApiError(err)) {
        setServerError(err.message);
      } else {
        setServerError('Impossibile salvare il profilo. Riprova.');
      }
    }
  }

  async function onSubmit(values: BillingProfileUpsertInput) {
    if (hasFattureEmesse && sdiChanged(values)) {
      setPendingValues(values);
      return;
    }
    await persist(values);
  }

  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Tab nav */}
        <div role="tablist" aria-label="Sezioni del profilo" className="flex gap-1 border-b">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`tab-${t.key}`}
              aria-selected={activeTab === t.key}
              aria-controls={`tabpanel-${t.key}`}
              onClick={() => selectTab(t.key)}
              className={cn(
                '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                activeTab === t.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Pannelli */}
        <div
          role="tabpanel"
          id="tabpanel-anagrafica"
          aria-labelledby="tab-anagrafica"
          hidden={activeTab !== 'anagrafica'}
        >
          {activeTab === 'anagrafica' ? <AnagraficaSection /> : null}
        </div>
        <div
          role="tabpanel"
          id="tabpanel-fatturazione"
          aria-labelledby="tab-fatturazione"
          hidden={activeTab !== 'fatturazione'}
        >
          {activeTab === 'fatturazione' ? <FatturazioneSection /> : null}
        </div>
        <div
          role="tabpanel"
          id="tabpanel-iban"
          aria-labelledby="tab-iban"
          hidden={activeTab !== 'iban'}
        >
          {activeTab === 'iban' ? (
            <IbanSection
              sepaAuthorized={sepaAuthorized}
              onSepaAuthorizedChange={setSepaAuthorized}
            />
          ) : null}
        </div>

        {/* Server error */}
        {serverError ? (
          <Alert variant="destructive">
            <AlertTitle>Errore di salvataggio</AlertTitle>
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        ) : null}

        {/* Successo */}
        {success ? (
          <Alert>
            <AlertTitle>Profilo salvato</AlertTitle>
            <AlertDescription>Le tue modifiche sono state registrate.</AlertDescription>
          </Alert>
        ) : null}

        <div className="flex justify-end">
          <Button type="submit" disabled={formState.isSubmitting}>
            {formState.isSubmitting ? 'Salvataggio…' : 'Salva profilo'}
          </Button>
        </div>
      </form>

      <SdiChangeConfirmDialog
        open={pendingValues !== null}
        onCancel={() => setPendingValues(null)}
        onConfirm={() => {
          const values = pendingValues;
          setPendingValues(null);
          if (values) {
            void persist(values);
          }
        }}
      />
    </FormProvider>
  );
}
